// "Export for Karen": the timetabling hand-off workbook, laid out to match the
// spreadsheet the timetabler works from (2026 Grid.xlsx). Two sheets:
//   Classes: Option | unfi | Faculty | TYPE | A..L | V | W  (Main Grid + Wednesday VET)
//   RUAH:    No. | Option | 1 | 2 | 3 | 4                   (RUAH grid, one column per term)
// PIT stores one class row per line; her sheet packs a subject onto one row
// with a teacher in each line column, spilling into "C2", "C3"… rows when two
// classes of the same subject share a line.
import type { ClassAllocation, StaffAllocationGrid } from '../types/grid';

const MAIN_LINES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
const S1_LINES = new Set(['A', 'B', 'C', 'D', 'E', 'F']);
const VET_LINES: Record<string, string> = { X: 'V', Y: 'W' }; // Wednesday VET grid -> her V/W columns
const CLASS_COLUMNS = [...MAIN_LINES, 'V', 'W'];
const RUAH_TERMS = ['A', 'B', 'C', 'D'];

// Colours lifted from her workbook (theme tints resolved to RGB).
const LABEL_FILL = 'FFFFFFE1';
const EMPTY_FILL = 'FFFFFFFF';
const S1_FILL = 'FFDCEDD5'; // accent6 80% tint
const S2_VCE_FILL = 'FFFFFF00';
const S2_OTHER_FILL = 'FF92D050';
const RUAH_FILLS = ['FFDCEDD5', 'FFCFECF7', 'FFFFFF00', 'FFFFFF00'];

export interface KarenRow {
  option: string;
  unfi: string;
  faculty: string;
  type: string;
  isVce: boolean;
  cells: Record<string, string>; // column letter -> teacher text
}

/** Teacher text for a line assignment. Multi-teacher assignments are stored as JSON. */
export function teacherText(raw: string | null | undefined): string {
  if (!raw) return '';
  const value = raw.trim();
  if (value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value) as Array<{ teacherCode?: string }>;
      return parsed.map(p => p.teacherCode).filter(Boolean).join('/');
    } catch {
      return value;
    }
  }
  return value;
}

function isVce(c: ClassAllocation): boolean {
  const year = Number(c.yearLevel);
  return year === 11 || year === 12 || (c.level || '').toUpperCase() === 'VCE';
}

function unfiFor(c: ClassAllocation): string {
  if (isVce(c)) return 'VCE';
  const stages = ['DISCOVER', 'EXPLORE', 'ENGAGE', 'FOCUS', 'REACH'];
  const prefix = c.className.split(' - ')[0].trim().toUpperCase();
  if (prefix === 'ENGAGE/EXPLORE') return 'ENG/EXP';
  if (stages.includes(prefix)) return prefix;
  const level = (c.level || '').toUpperCase(); // e.g. "BOOST READING" carries its stage only in level
  return stages.includes(level) ? level : '';
}

// Her sheet's short faculty names, where they differ from PIT's learning areas.
const FACULTY_NAMES: Record<string, string> = { 'VOCATIONAL MAJOR': 'VM', 'LEARNING DIVERSITY': 'LH' };

function facultyFor(c: ClassAllocation): string {
  const area = (c.curriculumArea || c.department || '').toUpperCase();
  return FACULTY_NAMES[area] ?? area;
}

/** Pack per-line class rows into subject rows, overflowing into C2, C3… */
function pack(
  groups: Map<string, { base: Omit<KarenRow, 'cells' | 'option'>; option: string; entries: Array<[string, string]> }>,
): KarenRow[] {
  const rows: KarenRow[] = [];
  for (const group of groups.values()) {
    const packed: Array<Record<string, string>> = [];
    for (const [column, teacher] of group.entries) {
      let slot = packed.findIndex(r => !(column in r));
      if (slot === -1) {
        packed.push({});
        slot = packed.length - 1;
      }
      packed[slot][column] = teacher;
    }
    packed.forEach((cells, i) => {
      rows.push({ ...group.base, option: i === 0 ? group.option : `${group.option} C${i + 1}`, cells });
    });
  }
  return rows;
}

export function buildClassRows(mainGrid: StaffAllocationGrid | undefined, vetGrid: StaffAllocationGrid | undefined): KarenRow[] {
  const groups = new Map<string, { base: Omit<KarenRow, 'cells' | 'option'>; option: string; entries: Array<[string, string]> }>();

  const add = (c: ClassAllocation, lineMap: (lineId: string) => string | null) => {
    if (c.isCompositeSubClass) return;
    const vce = isVce(c);
    const name = c.className.trim().toUpperCase();
    for (const [lineId, raw] of Object.entries(c.lineAssignments || {})) {
      const column = lineMap(lineId);
      const teacher = teacherText(raw);
      if (!column || !teacher) continue;
      // VCE subjects are split into S1 and S2 rows; everything else stays on one row.
      const semester = vce ? (S1_LINES.has(column) || column === 'V' || column === 'W' ? ' S1' : ' S2') : '';
      const option = `${name}${semester}`;
      const key = `${option}|${facultyFor(c)}`;
      if (!groups.has(key)) {
        groups.set(key, { option, base: { unfi: unfiFor(c), faculty: facultyFor(c), type: '', isVce: vce }, entries: [] });
      }
      groups.get(key)!.entries.push([column, teacher]);
    }
  };

  mainGrid?.classes.forEach(c => add(c, id => (MAIN_LINES.includes(id) ? id : null)));
  vetGrid?.classes.forEach(c => add(c, id => VET_LINES[id] ?? null));

  // Her sheet is ordered by faculty, then option name.
  return pack(groups).sort((a, b) => a.faculty.localeCompare(b.faculty) || a.option.localeCompare(b.option));
}

export interface RuahRow {
  option: string;
  terms: string[]; // four entries, Term 1..4
}

export function buildRuahRows(ruahGrid: StaffAllocationGrid | undefined): RuahRow[] {
  if (!ruahGrid) return [];
  const seen = new Map<string, number>();
  return ruahGrid.classes
    .filter(c => !c.isCompositeSubClass)
    .map(c => {
      // PIT names carry the Synergetic code ("EXPLORE - HEALTH / EXHEAHEAA"); her sheet doesn't.
      const base = c.className.split(' / ')[0].trim().toUpperCase();
      const n = (seen.get(base) ?? 0) + 1;
      seen.set(base, n);
      return {
        option: n === 1 ? base : `${base} C${n}`,
        terms: RUAH_TERMS.map(id => teacherText(c.lineAssignments?.[id])),
      };
    })
    .sort((a, b) => a.option.localeCompare(b.option));
}

export async function exportGridForKaren(grids: StaffAllocationGrid[], year: number): Promise<{ classRows: number; ruahRows: number }> {
  const ExcelJS = (await import('exceljs')).default;
  const yearGrids = grids.filter(g => g.year === year);
  const byName = (name: string) => yearGrids.find(g => g.name.trim().toLowerCase() === name.toLowerCase());
  const main = byName('Main Grid');
  const vet = byName('Wednesday VET');
  const ruah = byName('RUAH');

  const wb = new ExcelJS.Workbook();
  const border = { left: { style: 'thin' }, right: { style: 'thin' }, top: { style: 'thin' }, bottom: { style: 'thin' } } as const;
  const style = (cell: any, value: string, fill: string, size: number) => {
    cell.value = value;
    cell.font = { name: 'Verdana', family: 2, size, color: { argb: 'FF000000' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
    cell.border = border;
    cell.numFmt = '@';
  };

  // --- Classes ---
  const classes = wb.addWorksheet('Classes', {
    views: [{ state: 'frozen', ySplit: 1, zoomScale: 90 }],
    pageSetup: { paperSize: 9, orientation: 'landscape' },
  });
  classes.columns = [54.43, 10, 13.57, 7.71, 11.43, 13, 9.14, 16.71, 14.86, 5.57, 10.43, 12.71, 24.57, 16.14, 20.29, 13.86, 5.57, 5.57].map(width => ({ width }));
  const headers = ['Option', 'unfi', 'Faculty', 'TYPE', ...CLASS_COLUMNS];
  const header = classes.getRow(1);
  header.height = 15;
  headers.forEach((h, i) => style(header.getCell(i + 1), h, LABEL_FILL, i < 4 ? 9 : 10));

  const classRows = buildClassRows(main, vet);
  classRows.forEach((r, idx) => {
    const row = classes.getRow(idx + 2);
    [r.option, r.unfi, r.faculty, r.type].forEach((v, i) => style(row.getCell(i + 1), v, LABEL_FILL, 9));
    CLASS_COLUMNS.forEach((col, i) => {
      const teacher = r.cells[col] || '';
      let fill = EMPTY_FILL;
      if (teacher) fill = S1_LINES.has(col) || col === 'V' || col === 'W' ? S1_FILL : r.isVce ? S2_VCE_FILL : S2_OTHER_FILL;
      style(row.getCell(i + 5), teacher, fill, 10);
    });
  });
  classes.autoFilter = { from: 'A1', to: `R${classRows.length + 1}` };

  // --- RUAH ---
  const ruahSheet = wb.addWorksheet('RUAH', { pageSetup: { paperSize: 9, orientation: 'landscape' } });
  ruahSheet.columns = [4, 51.86, 16.57, 15.43, 15.43, 15.43].map(width => ({ width }));
  const ruahHeader = ruahSheet.getRow(1);
  ruahHeader.height = 15;
  ['No.', 'Option', '1', '2', '3', '4'].forEach((h, i) => style(ruahHeader.getCell(i + 1), h, LABEL_FILL, 10));
  const ruahRows = buildRuahRows(ruah);
  ruahRows.forEach((r, idx) => {
    const row = ruahSheet.getRow(idx + 2);
    row.height = 15;
    style(row.getCell(1), String(idx + 1), LABEL_FILL, 10);
    style(row.getCell(2), r.option, LABEL_FILL, 10);
    r.terms.forEach((t, i) => style(row.getCell(i + 3), t, t ? RUAH_FILLS[i] : EMPTY_FILL, 10));
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${year} Grid.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return { classRows: classRows.length, ruahRows: ruahRows.length };
}
